import React, {useRef} from 'react';
import {CSSTransition} from 'react-transition-group';
import {callAction, useFluxStore} from 'client/framework';

import {Button, Text, Logo, LoadingSpinner} from '@albert-io/atomic';

import masqueradePanelStore from '../Masquerade.store';
import masqueradeActions from '../Masquerade.actions';

import './masquerade-panel.scss';

const SLIDE_DURATION_MS = 250;

export default function MasqueradePanel() {
  const nodeRef = useRef<HTMLDivElement>(null);
  const isMasquerading = useFluxStore(masqueradePanelStore, (store) => store.isMasquerading());
  // Each selector returns a primitive — useFluxStore uses useSyncExternalStore, which has no
  // equality fn, so an object/array return would loop. Readiness is guarded inside the selector
  // so getUser()/getSchool() are never called before their query resolves (matches the class).
  const isAccountReady = useFluxStore(
    masqueradePanelStore,
    (store) =>
      store.isMasquerading() &&
      store.getUserQuery().isResourceReady() &&
      store.getSchoolQuery().isResourceReady()
  );
  const fullName = useFluxStore(masqueradePanelStore, (store) =>
    store.isMasquerading() && store.getUserQuery().isResourceReady()
      ? store.getUser().getFullName()
      : null
  );
  const schoolName = useFluxStore(masqueradePanelStore, (store) =>
    store.isMasquerading() && store.getSchoolQuery().isResourceReady()
      ? store.getSchool().getName()
      : null
  );

  const handleExit = () => {
    callAction(masqueradeActions.DISABLE_MASQUERADING);
  };

  const panelContents = isMasquerading ? (
    <>
      <div className='masquerade-panel__logo'>
        <Logo width='112px' color='white' />
      </div>
      <div className='flex items-center text-right'>
        {isAccountReady ? (
          <Text as='div' color='primary-inverse-static' className='m-x1'>
            <b>{fullName}</b>
            <br />
            <small>{schoolName}</small>
          </Text>
        ) : (
          <LoadingSpinner
            className='masquerade-panel__spinner'
            assistiveText='Fetching account details...'
          />
        )}
        <Button className='ml-x1' color='blend-dark' onClick={handleExit}>
          Back to my account
        </Button>
      </div>
    </>
  ) : null;

  return (
    <CSSTransition
      in={isMasquerading}
      nodeRef={nodeRef}
      classNames='masquerade-panel-slide'
      timeout={SLIDE_DURATION_MS}
      mountOnEnter
      unmountOnExit
    >
      <div ref={nodeRef} className='masquerade-panel p-x1'>
        {panelContents}
      </div>
    </CSSTransition>
  );
}
